import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Phone } from 'lucide-react'
import { Alert, Avatar, Button, Card, Input, StatusBadge } from '@rallyshift/design-system'
import api from '../../api/client'
import { statusLabel } from '../../utils/status'
import { formatTimeRangeToLocal } from '../../utils/timezone'

type Attendance = 'expected' | 'checked-in' | 'late' | 'no-show' | 'checked-out'

interface RosterEntry {
  id: string
  attendance: Attendance
  checked_in_at: string | null
  shift: {
    id: string
    title: string
    start_time: string
    end_time: string
    max_volunteers: number
    current_volunteers: number
  }
  volunteer?: {
    id: string
    first_name: string | null
    last_name: string | null
    email: string
    phone_number: string | null
  }
}

const COUNTERS: { key: Attendance; glyph: string; label: string }[] = [
  { key: 'expected', glyph: '\u25CB', label: 'verwacht' },
  { key: 'checked-in', glyph: '\u25C9', label: 'ingecheckt' },
  { key: 'late', glyph: '\u25D4', label: 'te laat' },
  { key: 'no-show', glyph: '\u2715', label: 'no-show' },
]

function today(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function name(v: RosterEntry['volunteer']): string {
  if (!v) return 'Onbekend'
  return `${v.first_name ?? ''} ${v.last_name ?? ''}`.trim() || v.email
}

/**
 * The attendance board for the day itself (design screen 1h). Everything is one tap:
 * on an event day the organiser is standing up, holding a phone, and being talked to.
 */
const EventDayPage: React.FC = () => {
  const [date, setDate] = useState(today())
  const [roster, setRoster] = useState<RosterEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRoster(await api.shifts.getDayRoster(date))
      setError('')
    } catch (e: any) {
      setError(e.message || 'De bezetting kon niet worden geladen.')
    } finally {
      setLoading(false)
    }
  }, [date])

  useEffect(() => {
    load()
  }, [load])

  const counts = useMemo(() => {
    const tally: Record<string, number> = {}
    roster.forEach((r) => {
      tally[r.attendance] = (tally[r.attendance] ?? 0) + 1
    })
    return tally
  }, [roster])

  /** Shifts running now that have fewer people checked in than places. */
  const understaffed = useMemo(() => {
    const now = Date.now()
    const byShift = new Map<string, { title: string; max: number; present: number }>()
    roster.forEach((r) => {
      const start = new Date(r.shift.start_time).getTime()
      const end = new Date(r.shift.end_time).getTime()
      if (now < start || now > end) return
      const entry = byShift.get(r.shift.id) ?? {
        title: r.shift.title,
        max: r.shift.max_volunteers,
        present: 0,
      }
      if (r.attendance === 'checked-in' || r.attendance === 'checked-out') entry.present += 1
      byShift.set(r.shift.id, entry)
    })
    return Array.from(byShift.values()).filter((s) => s.present < s.max)
  }, [roster])

  const setAttendance = async (entry: RosterEntry, attendance: Attendance) => {
    setBusyId(entry.id)
    try {
      await api.shifts.setAttendance(entry.id, attendance)
      setRoster((prev) =>
        prev.map((r) => (r.id === entry.id ? { ...r, attendance } : r)),
      )
    } catch (e: any) {
      setError(e.message || 'Aanwezigheid bijwerken is niet gelukt.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-display-lg text-text-primary">Evenementdag</h1>
          <p className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-md text-text-secondary">
            {COUNTERS.map((c) => (
              <span key={c.key}>
                {c.glyph} {counts[c.key] ?? 0} {c.label}
              </span>
            ))}
          </p>
        </div>
        <Input
          label="Dag"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          containerClassName="sm:w-48"
        />
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      {understaffed.map((s) => (
        <Alert key={s.title} variant="warning">
          <strong>
            {s.title} staat er nu met {s.present} van {s.max}.
          </strong>{' '}
          Bel een invaller of verplaats iemand van een rustigere post.
        </Alert>
      ))}

      <Card>
        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-action-primary border-r-transparent" />
          </div>
        ) : roster.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-muted">
            Er is niemand ingepland op deze dag.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {roster.map((entry) => {
              const checkedIn = entry.attendance === 'checked-in'
              const done = entry.attendance === 'checked-out'
              return (
                <li
                  key={entry.id}
                  className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:gap-4"
                >
                  <Avatar name={name(entry.volunteer)} size={34} />

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-md font-semibold text-text-primary">
                      {name(entry.volunteer)}
                    </p>
                    <p className="truncate text-sm text-text-muted">
                      {entry.shift.title} ·{' '}
                      {formatTimeRangeToLocal(entry.shift.start_time, entry.shift.end_time)}
                    </p>
                  </div>

                  <StatusBadge
                    dimension="attendance"
                    status={entry.attendance}
                    label={statusLabel('attendance', entry.attendance)}
                  />

                  <div className="flex flex-wrap gap-2">
                    {!checkedIn && !done && (
                      <Button
                        loading={busyId === entry.id}
                        onClick={() => setAttendance(entry, 'checked-in')}
                      >
                        Check in
                      </Button>
                    )}
                    {checkedIn && (
                      <Button
                        variant="secondary"
                        loading={busyId === entry.id}
                        onClick={() => setAttendance(entry, 'checked-out')}
                      >
                        Check uit
                      </Button>
                    )}
                    {entry.attendance === 'expected' && (
                      <>
                        <Button
                          variant="secondary"
                          disabled={busyId === entry.id}
                          onClick={() => setAttendance(entry, 'late')}
                        >
                          Te laat
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={busyId === entry.id}
                          onClick={() => setAttendance(entry, 'no-show')}
                        >
                          No-show
                        </Button>
                      </>
                    )}
                    {entry.volunteer?.phone_number && (
                      <a
                        href={`tel:${entry.volunteer.phone_number}`}
                        className="inline-flex min-h-touch-min items-center gap-2 rounded-md border-1.5 border-border-strong px-4 text-[15px] font-semibold text-text-primary hover:bg-surface-sunken"
                      >
                        <Phone className="h-4 w-4" />
                        Bel {entry.volunteer.first_name || 'vrijwilliger'}
                      </a>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>
    </div>
  )
}

export default EventDayPage
